import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Search, Calendar, Settings, BarChart3, CheckCircle2 } from 'lucide-react';
import { format, isToday, isTomorrow, parseISO } from 'date-fns';
import mixpanel from 'mixpanel-browser';
import { purpleColors, violetColors, slateColors, Section, Container, fontFamily } from '../ContentPages/AppLandingPage/styles';
import LocationPicker from '../SearchControls/LocationPicker';
import DateTimeCard from '../SearchControls/DateTimeCard';
import OptionsCard from '../SearchControls/OptionsCard';
import { getStationImageURL } from '../../utils/stationImage';
import ChuuchuuIcon from '../ChuuchuuIcon';
import SwitchButton from '../SearchControls/SwitchButton';
import { useTranslation } from 'react-i18next';

const HeroSection = styled(Section)`
  position: relative;
  overflow-x: hidden;
  background: linear-gradient(to bottom right, ${purpleColors[50]}, white, ${violetColors[500]}15);
  padding: 2rem 0.75rem 3rem;
  
  @media (min-width: 640px) {
    padding: 3rem 1.5rem 4rem;
  }
  
  @media (min-width: 768px) {
    padding: 4rem 3rem 5rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(to right, ${purpleColors[500]}08 1px, transparent 1px),
      linear-gradient(to bottom, ${purpleColors[500]}08 1px, transparent 1px);
    background-size: 24px 24px;
    pointer-events: none;
  }
`;

const HeroContainer = styled(Container)`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: center;
    gap: 4rem;
  }
`;

const HeroText = styled.div`
  text-align: center;
  
  @media (min-width: 1024px) {
    text-align: left;
    flex: 1;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${slateColors[900]};
  margin-bottom: 1rem;
  line-height: 1.1;
  
  @media (min-width: 640px) {
    font-size: 2.5rem;
  }
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3.5rem;
  }
`;

const TitleHighlight = styled.span`
  background: linear-gradient(to right, ${purpleColors[600]}, ${violetColors[600]});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-size: 1rem;
  color: ${slateColors[600]};
  margin: 0 auto;
  max-width: 42rem;
  line-height: 1.6;
  
  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  max-width: 32rem;
  margin: 1.5rem auto 0;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
    max-width: 100%;
  }
  
  @media (min-width: 1024px) {
    margin: 0.75rem 0 0;
    max-width: 100%;
    gap: 1rem;
  }
`;

const DesktopFeaturesGrid = styled(FeaturesGrid)`
  display: none;
  margin: 2.5rem auto 0;
  
  @media (min-width: 1024px) {
    display: grid;
  }
`;

const MobileFeaturesGrid = styled(FeaturesGrid)`
  display: grid;
  max-width: 48rem;
  margin: 1.5rem auto 0;
  
  @media (min-width: 1024px) {
    display: none;
  }
`;

const FeatureCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem;
  border-radius: 0.75rem;
  background: white;
  border: 1px solid ${slateColors[200]};
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border-color: ${purpleColors[200]};
    transform: translateY(-2px);
  }
  
  @media (min-width: 640px) {
    padding: 1rem;
    gap: 1rem;
  }
`;

const IconWrapper = styled.div<{ $color: 'purple' | 'violet' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.2s;
  
  ${FeatureCard}:hover & {
    transform: scale(1.1);
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${props => props.$color === 'purple' ? purpleColors[600] : violetColors[600]};
  }
  
  @media (min-width: 640px) {
    svg {
      width: 1.5rem;
      height: 1.5rem;
    }
  }
`;

const ChuuchuuIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.2s;
  
  ${FeatureCard}:hover & {
    transform: scale(1.1);
  }
`;

const CardTitle = styled.h3`
  font-family: ${fontFamily};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${slateColors[900]};
  margin: 0;
  line-height: 1.4;
  overflow-wrap: break-word;
  min-width: 0;
  
  @media (min-width: 640px) {
    font-size: 1rem;
  }
`;

const SearchCard = styled.div`
  background: white;
  border-radius: 1.5rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 48rem;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  @media (min-width: 768px) {
    border-radius: 2rem;
  }
  
  @media (min-width: 1024px) {
    max-width: none;
    flex: 1;
    margin: 0;
  }
`;

const SearchForm = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  flex: 1;
`;

const LocationsRow = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  width: 100%;
  height: 12rem;
  flex-shrink: 0;
  
  @media (min-width: 640px) {
    height: 14rem;
  }
  
  @media (min-width: 768px) {
    height: 16rem;
  }
  
  @media (min-width: 1024px) {
    height: 18rem;
  }
`;

const LocationButton = styled.button<{ $imageUrl?: string; $isLoading?: boolean; $type?: 'from' | 'to' }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 0;
  width: 50%;
  height: 100%;
  min-width: 0;
  background: ${props => {
    if (props.$imageUrl && !props.$isLoading) {
      return `url(${props.$imageUrl})`;
    }
    // Different default backgrounds for origin and destination
    if (props.$type === 'from') {
      return `linear-gradient(to bottom right, ${purpleColors[100]}, ${purpleColors[50]})`;
    } else if (props.$type === 'to') {
      return `linear-gradient(to bottom right, ${violetColors[500]}20, ${purpleColors[100]})`;
    }
    return slateColors[50];
  }};
  background-size: cover;
  background-position: center;
  border: none;
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  position: relative;
  overflow: hidden;
  
  &:first-child {
    border-right: 1px solid ${slateColors[200]};
    border-top-left-radius: 1.5rem;
    
    @media (min-width: 768px) {
      border-top-left-radius: 2rem;
    }
  }
  
  &:last-child {
    border-top-right-radius: 1.5rem;
    
    @media (min-width: 768px) {
      border-top-right-radius: 2rem;
    }
  }
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: inset 0 0 0 2px ${purpleColors[300]};
  }
  
  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 3px ${purpleColors[500]};
  }
`;

const LocationButtonContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  padding: 1rem 1.25rem;
  box-sizing: border-box;
  min-width: 0;
  
  @media (min-width: 640px) {
    padding: 1.25rem 1.5rem;
  }
`;

const StationNameCard = styled.div`
  background: white;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(4px);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
  
  @media (min-width: 640px) {
    padding: 0.875rem 1.25rem;
  }
`;

const LocationName = styled.div<{ $type?: 'from' | 'to' }>`
  font-family: ${fontFamily};
  font-size: 1.125rem;
  font-weight: 600;
  color: ${slateColors[900]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  min-width: 0;
  display: flex;
  gap: 0.375rem;
  
  @media (min-width: 640px) {
    font-size: 1.25rem;
  }
`;

const LocationLabel = styled.span`
  color: ${slateColors[500]};
  font-weight: 500;
  flex-shrink: 0;
`;

const LocationStationName = styled.span`
  color: ${slateColors[900]};
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const SwitchButtonWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
`;

const ControlsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.75rem;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  padding: 1.25rem 1.25rem 0.75rem 1.25rem;
  
  @media (min-width: 640px) {
    gap: 1rem;
    padding: 2rem 2rem 1rem 2rem;
  }
  
  @media (min-width: 768px) {
    padding: 2.5rem 2.5rem 1.25rem 2.5rem;
  }
`;

const DateTimeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1rem;
  background: ${slateColors[50]};
  border: 2px solid ${slateColors[200]};
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  
  &:hover {
    border-color: ${purpleColors[300]};
    background: ${slateColors[100]};
  }
  
  &:focus {
    outline: none;
    border-color: ${purpleColors[500]};
    box-shadow: 0 0 0 3px ${purpleColors[100]};
  }
  
  @media (min-width: 640px) {
    padding: 1.25rem 1.5rem;
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${purpleColors[600]};
    flex-shrink: 0;
  }
`;

const DateTimeText = styled.div`
  font-family: ${fontFamily};
  font-size: 1.0625rem;
  font-weight: 500;
  color: ${slateColors[700]};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
`;

const OptionsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: ${slateColors[50]};
  border: 2px solid ${slateColors[200]};
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 3.5rem;
  
  &:hover {
    border-color: ${purpleColors[300]};
    background: ${slateColors[100]};
  }
  
  &:focus {
    outline: none;
    border-color: ${purpleColors[500]};
    box-shadow: 0 0 0 3px ${purpleColors[100]};
  }
  
  @media (min-width: 640px) {
    padding: 1.25rem;
    min-width: 4rem;
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${purpleColors[600]};
  }
`;

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: linear-gradient(to right, ${purpleColors[600]}, ${violetColors[600]});
  color: white;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 1.125rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  margin: 0 1.25rem 1.25rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  
  &:hover {
    background: linear-gradient(to right, ${purpleColors[700]}, ${violetColors[600]});
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
  
  @media (min-width: 640px) {
    padding: 1.25rem 2.5rem;
    font-size: 1.25rem;
    margin: 0 2rem 2rem;
  }
  
  @media (min-width: 768px) {
    margin: 0 2.5rem 2.5rem;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
  box-sizing: border-box;
`;

const OverlayContent = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 0;
`;

interface SearchHeroProps {
  setOrigin: (id: string) => void;
  setDestination: (id: string) => void;
  setDate: (date: string) => void;
  setOriginName: React.Dispatch<React.SetStateAction<string>>;
  setDestinationName: React.Dispatch<React.SetStateAction<string>>;
  setTripType: (type: 'departure' | 'arrival') => void;
  setOptions: (options: Record<string, string>) => void;
  fetchJourneys: () => void;
  originId: string;
  destinationId: string;
  originName: string;
  destinationName: string;
  date: string;
  tripType: 'departure' | 'arrival';
  options: Record<string, string>;
}

const defaultOptions: Record<string, any> = {
  transfers: -1,
  transferTime: 0,
  accessibility: 'none',
  bike: false,
  longdistance: true,
  regional: true,
  suburban: true,
  local: true,
};

interface Location {
  id: string;
  name: string;
}

const SearchHero = ({
  setOrigin,
  setDestination,
  setOriginName,
  setDestinationName,
  setDate,
  setTripType,
  setOptions,
  fetchJourneys,
  originId,
  destinationId,
  originName,
  destinationName,
  date,
  tripType,
  options,
}: SearchHeroProps) => {
  const [isLocationPickerOpen, setLocationPickerOpen] = useState(false);
  const [locationType, setLocationType] = useState<'from' | 'to'>('from');
  const [isDateTimeCardOpen, setDateTimeCardOpen] = useState(false);
  const [isOptionsCardOpen, setOptionsCardOpen] = useState(false);
  const [localOptions, setLocalOptions] = useState<Record<string, string>>(() => {
    const savedOptions = localStorage.getItem('journeyOptions');
    return savedOptions ? JSON.parse(savedOptions) : defaultOptions;
  });
  const [recentlyChosen, setRecentlyChosen] = useState<{ name: string; id: string }[]>(
    JSON.parse(localStorage.getItem('recentlyChosen') || '[]')
  );
  const { t } = useTranslation();
  const [originImageUrl, setOriginImageUrl] = useState<string | null>(null);
  const [destinationImageUrl, setDestinationImageUrl] = useState<string | null>(null);
  const [originImageLoading, setOriginImageLoading] = useState(false);
  const [destinationImageLoading, setDestinationImageLoading] = useState(false);

  const loadFromLocalStorage = useCallback(() => {
    const savedOrigin = localStorage.getItem('origin');
    const savedDestination = localStorage.getItem('destination');
    const savedOptions = localStorage.getItem('journeyOptions');

    if (savedOrigin && !originId) {
      const parsed = JSON.parse(savedOrigin);
      setOrigin(parsed.id);
      setOriginName(parsed.name);
    } else if (!originId && !savedOrigin) {
      // Set default origin if nothing is saved
      setOrigin('8700014');
      setOriginName('Paris Nord');
    }

    if (savedDestination && !destinationId) {
      const parsed = JSON.parse(savedDestination);
      setDestination(parsed.id);
      setDestinationName(parsed.name);
    } else if (!destinationId && !savedDestination) {
      // Set default destination if nothing is saved
      setDestination('8011160');
      setDestinationName('Berlin Hbf');
    }

    if (savedOptions && Object.keys(options).length === 0) {
      setOptions(JSON.parse(savedOptions));
    } else {
      setOptions(defaultOptions);
    }
  }, [setOrigin, setOriginName, setDestination, setDestinationName, setOptions, originId, destinationId, options]);

  useEffect(() => {
    if (!originId && !destinationId) {
      loadFromLocalStorage();
    }
  }, [originId, destinationId, loadFromLocalStorage]);

  useEffect(() => {
    if (Object.keys(options).length === 0) {
      loadFromLocalStorage();
    }
  }, [options, loadFromLocalStorage]);

  // Load origin station image (non-blocking)
  useEffect(() => {
    if (!originId || !originName) {
      setOriginImageUrl(null);
      return;
    }

    let cancelled = false;
    setOriginImageLoading(true);

    // Load image asynchronously without blocking UI
    getStationImageURL(originName, originId)
      .then((url) => {
        if (!cancelled) {
          setOriginImageUrl(url);
          setOriginImageLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOriginImageUrl(null);
          setOriginImageLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [originId, originName]);

  // Load destination station image (non-blocking)
  useEffect(() => {
    if (!destinationId || !destinationName) {
      setDestinationImageUrl(null);
      return;
    }

    let cancelled = false;
    setDestinationImageLoading(true);

    // Load image asynchronously without blocking UI
    getStationImageURL(destinationName, destinationId)
      .then((url) => {
        if (!cancelled) {
          setDestinationImageUrl(url);
          setDestinationImageLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDestinationImageUrl(null);
          setDestinationImageLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [destinationId, destinationName]);

  useEffect(() => {
    if (originId && originName) {
      localStorage.setItem('origin', JSON.stringify({ id: originId, name: originName }));
    }
    if (destinationId && destinationName) {
      localStorage.setItem('destination', JSON.stringify({ id: destinationId, name: destinationName }));
    }
    if (date) {
      localStorage.setItem('localDate', date);
    }
    if (tripType) {
      localStorage.setItem('localTripType', tripType);
    }
    if (Object.keys(options).length > 0) {
      localStorage.setItem('journeyOptions', JSON.stringify(options));
    }
    localStorage.setItem('recentlyChosen', JSON.stringify(recentlyChosen));
  }, [originId, originName, destinationId, destinationName, options, recentlyChosen, date, tripType]);

  const openOverlay = (type: 'from' | 'to') => {
    setLocationType(type);
    setLocationPickerOpen(true);
  };

  const closeOverlay = () => {
    setLocationPickerOpen(false);
    setOptionsCardOpen(false);
    setDateTimeCardOpen(false);
  };

  const handleLocationSelect = (location: Location) => {
    setRecentlyChosen((prev) => [location, ...prev].slice(0, 10));

    if (locationType === 'from') {
      setOrigin(location.id);
      setOriginName(location.name);
    } else {
      setDestination(location.id);
      setDestinationName(location.name);
    }

    closeOverlay();
  };

  const handleSwitchLocations = () => {
    setOrigin(destinationId);
    setDestination(originId);
    setOriginName(destinationName);
    setDestinationName(originName);
  };

  const handleDateTime = (newDateTime: string, newType: 'departure' | 'arrival') => {
    setDate(newDateTime);
    setTripType(newType);
    localStorage.setItem('localDate', newDateTime);
    localStorage.setItem('localTripType', newType);
    setDateTimeCardOpen(false);
  };

  const getFormattedDateTime = () => {
    if (!date) {
      return t('homeScreen.searchHero.departNow');
    }

    try {
      const parsedDate = parseISO(date);
      if (isNaN(parsedDate.getTime())) {
        return t('homeScreen.searchHero.departNow');
      }

      const dateStr = isToday(parsedDate)
        ? t('common.today')
        : isTomorrow(parsedDate)
          ? t('common.tomorrow')
          : format(parsedDate, 'dd/MM');

      const timeStr = format(parsedDate, 'HH:mm');
      return `${tripType === 'arrival' ? t('homeScreen.searchHero.arrive') : t('homeScreen.searchHero.depart')} ${dateStr} ${t('common.at')} ${timeStr}`;
    } catch {
      return t('homeScreen.searchHero.departNow');
    }
  };

  const handleOptions = (newOptions: Record<string, string>) => {
    setLocalOptions(newOptions);
    setOptions(newOptions);
    localStorage.setItem('journeyOptions', JSON.stringify(newOptions));
    setOptionsCardOpen(false);
  };

  const handleSearchClick = () => {
    mixpanel.track('Search Button Clicked', {
      originName,
      destinationName,
      date,
      tripType,
      options: localOptions,
    });

    fetchJourneys();
  };

  return (
    <HeroSection>
      <HeroContainer>
        <HeroText>
          <Title>
            {t('homeScreen.searchHero.titleHighlight') === 'Sorglos' ? (
              <>
                <TitleHighlight>{t('homeScreen.searchHero.titleHighlight')}</TitleHighlight>
                <br />
                {t('homeScreen.searchHero.title')}
              </>
            ) : (
              <>
                {t('homeScreen.searchHero.title')}
                <br />
                <TitleHighlight>{t('homeScreen.searchHero.titleHighlight')}</TitleHighlight>
              </>
            )}
          </Title>
          <Description>
            {t('homeScreen.searchHero.description')}
          </Description>
          
          <DesktopFeaturesGrid>
            <FeatureCard>
              <ChuuchuuIconWrapper>
                <ChuuchuuIcon color="purple" />
              </ChuuchuuIconWrapper>
              <CardTitle>{t('homeScreen.searchHero.findReliableJourneys')}</CardTitle>
            </FeatureCard>

            <FeatureCard>
              <IconWrapper $color="purple">
                <BarChart3 />
              </IconWrapper>
              <CardTitle>{t('homeScreen.searchHero.detailedDelayStatistics')}</CardTitle>
            </FeatureCard>
            
            <FeatureCard>
              <IconWrapper $color="violet">
                <CheckCircle2 />
              </IconWrapper>
              <CardTitle>{t('homeScreen.searchHero.noMoreGuesswork')}</CardTitle>
            </FeatureCard>
          </DesktopFeaturesGrid>
        </HeroText>

        <SearchCard>
          <SearchForm>
            <LocationsRow>
              <LocationButton 
                onClick={() => openOverlay('from')}
                $imageUrl={originImageUrl || undefined}
                $isLoading={originImageLoading}
                $type="from"
              >
                <LocationButtonContent>
                  <StationNameCard>
                    <LocationName $type="from">
                      {originName ? (
                        <>
                          <LocationLabel>{t('searchControls.from')}</LocationLabel>
                          <LocationStationName>{originName}</LocationStationName>
                        </>
                      ) : (
                        <LocationStationName>{t('homeScreen.searchHero.selectStation')}</LocationStationName>
                      )}
                    </LocationName>
                  </StationNameCard>
                </LocationButtonContent>
              </LocationButton>

              <SwitchButtonWrapper>
                <SwitchButton onClick={handleSwitchLocations} aria-label={t('homeScreen.searchHero.switchOriginDestination')} />
              </SwitchButtonWrapper>

              <LocationButton 
                onClick={() => openOverlay('to')}
                $imageUrl={destinationImageUrl || undefined}
                $isLoading={destinationImageLoading}
                $type="to"
              >
                <LocationButtonContent>
                  <StationNameCard>
                    <LocationName $type="to">
                      {destinationName ? (
                        <>
                          <LocationLabel>{t('searchControls.to')}</LocationLabel>
                          <LocationStationName>{destinationName}</LocationStationName>
                        </>
                      ) : (
                        <LocationStationName>{t('homeScreen.searchHero.selectStation')}</LocationStationName>
                      )}
                    </LocationName>
                  </StationNameCard>
                </LocationButtonContent>
              </LocationButton>
            </LocationsRow>

            <ControlsRow>
              <DateTimeButton onClick={() => setDateTimeCardOpen(true)}>
                <Calendar />
                <DateTimeText>{getFormattedDateTime()}</DateTimeText>
              </DateTimeButton>
              <OptionsButton onClick={() => setOptionsCardOpen(true)} aria-label={t('homeScreen.searchHero.searchOptions')}>
                <Settings />
              </OptionsButton>
            </ControlsRow>

            <SearchButton onClick={handleSearchClick}>
              <Search />
              {t('homeScreen.searchHero.findReliableTrains')}
            </SearchButton>
          </SearchForm>
        </SearchCard>

        <MobileFeaturesGrid>
          <FeatureCard>
            <ChuuchuuIconWrapper>
              <ChuuchuuIcon color="purple" />
            </ChuuchuuIconWrapper>
            <CardTitle>{t('homeScreen.searchHero.findReliableJourneys')}</CardTitle>
          </FeatureCard>

          <FeatureCard>
            <IconWrapper $color="purple">
              <BarChart3 />
            </IconWrapper>
            <CardTitle>{t('homeScreen.searchHero.detailedDelayStatistics')}</CardTitle>
          </FeatureCard>
          
          <FeatureCard>
            <IconWrapper $color="violet">
              <CheckCircle2 />
            </IconWrapper>
            <CardTitle>{t('homeScreen.searchHero.noMoreGuesswork')}</CardTitle>
          </FeatureCard>
        </MobileFeaturesGrid>
      </HeroContainer>

      {(isLocationPickerOpen || isOptionsCardOpen || isDateTimeCardOpen) && (
        <Overlay onClick={closeOverlay}>
          <OverlayContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            {isLocationPickerOpen && (
              <LocationPicker
                onClose={closeOverlay}
                onLocationSelect={handleLocationSelect}
                recentlyChosen={recentlyChosen}
              />
            )}
            {isOptionsCardOpen && (
              <OptionsCard
                onClose={closeOverlay}
                onApply={handleOptions}
                initialOptions={localOptions}
              />
            )}
            {isDateTimeCardOpen && (
              <DateTimeCard
                onClose={closeOverlay}
                onApply={handleDateTime}
                initialDate={date ? date.split('T')[0] : ''}
                initialTime={date ? date.split('T')[1] : ''}
                initialType={tripType}
              />
            )}
          </OverlayContent>
        </Overlay>
      )}
    </HeroSection>
  );
};

export default SearchHero;

